import { InputSelect, InputWrapper } from '@vrplatform/components';
import { Icon } from '@vrplatform/icons';
import { Box } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  EXPENSE_TRANSACTION_LINE_ACCOUNT_CLASSIFICATION_ORDER,
  useEnabledFeatures,
  useTransactionLineAccountOptionsQuery,
} from '@vrplatform/data';
import { Controller, useWatch } from 'react-hook-form';
import { AddAccountModal } from '../../../accounts/_components';
import { useExpenseForm } from '../useExpenseForm';

export const ExpenseLineAccount = ({
  index,
  disabled,
}: {
  index: number;
  disabled: boolean;
}) => {
  const methods = useExpenseForm();
  const { OPEX } = useEnabledFeatures();
  const [opened, handlers] = useDisclosure();
  const party = useWatch({
    control: methods.control,
    name: `lines.${index}.party`,
  });

  const queryData = useTransactionLineAccountOptionsQuery({
    party,
    classificationOrder: EXPENSE_TRANSACTION_LINE_ACCOUNT_CLASSIFICATION_ORDER,
    transactionType: 'expense',
    allowOwnerExpensePayoutDistribution: !OPEX,
  });

  return (
    <Box>
      <Controller
        control={methods.control}
        name={`lines.${index}.accountId`}
        rules={{
          required: 'Account is required',
          validate: (value) => {
            if (!value) return true;

            const option = queryData.data?.find((item) => item.value === value);

            if (!option?.disabled) return true;

            return typeof option.disabled === 'string'
              ? option.disabled
              : 'This account cannot be selected.';
          },
        }}
        render={({ field, fieldState, formState }) => {
          const account =
            queryData.data?.find((x) => x.value === field.value) ?? null;

          const loading = queryData.isLoading;
          const hasListingSelectionError = !!(
            formState.errors.lines?.[index]?.listingId ||
            formState.errors.lines?.[index]?.reservationId
          );

          const handleAccountChange = (accountId: string | null) => {
            field.onChange(accountId);
            if (hasListingSelectionError) {
              void methods.trigger(`lines.${index}.reservationId`);
              void methods.trigger(`lines.${index}.listingId`);
            }
          };

          return (
            <>
              <AddAccountModal
                closeModal={handlers.close}
                opened={opened}
                onAccountAdded={handleAccountChange}
              />
              <InputWrapper error={fieldState.error?.message} required>
                <InputSelect
                  type="single"
                  data-testid={field.name}
                  name={field.name}
                  value={account}
                  setValue={(v) => handleAccountChange(v?.value || null)}
                  data={{
                    options: queryData.data || [],
                    loading,
                  }}
                  inputProps={{
                    error: !!fieldState.error,
                    placeholder: 'Select account',
                    loadingQuery: loading && !!field.value,
                    // withClearButton: true,
                    width: 250,
                    disabled:
                      field.disabled ||
                      methods.context.lock?.expense === 'locked' ||
                      disabled,
                  }}
                  dropdownProps={{
                    width: 350,
                  }}
                  customBottomActions={[
                    {
                      id: 'add-account',
                      label: 'Add account',
                      onSubmit: () => handlers.open(),
                      description:
                        "Add a new account in case you're missing one",
                      icon: <Icon icon="PlusIcon" size={16} />,
                    },
                  ]}
                />
              </InputWrapper>
            </>
          );
        }}
      />
    </Box>
  );
};
